import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  AlertTriangle, 
  Home, 
  User, 
  Calendar, 
  ChevronRight,
  Image,
  MessageSquare,
  CheckCircle,
  Search,
  X,
  Trash2,
  CheckSquare,
  Square,
  Loader2
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
} from "../components/ui/dialog";
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Signalements = () => {
  const navigate = useNavigate();
  const [signalements, setSignalements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showPhotoDialog, setShowPhotoDialog] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  const token = localStorage.getItem('token');

  const fetchSignalements = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/signalements`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSignalements(data);
      }
    } catch (error) {
      console.error('Error fetching signalements:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchSignalements();
  }, [fetchSignalements]);

  const markAsResolved = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}/api/signalements/${taskId}/resolve`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchSignalements();
        toast.success('Signalement marqué comme traité');
      }
    } catch (error) {
      console.error('Error resolving signalement:', error);
      toast.error('Erreur lors du traitement');
    }
  };

  const deleteSignalement = async (taskId) => {
    try {
      const response = await fetch(`${API_URL}/api/signalements/${taskId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        fetchSignalements();
        toast.success('Signalement supprimé');
      }
    } catch (error) {
      console.error('Error deleting signalement:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Bulk actions
  const handleSelectAll = () => {
    if (selectedIds.length === filteredSignalements.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredSignalements.map(s => s.task_id));
    }
  };

  const handleSelectOne = (taskId) => {
    if (selectedIds.includes(taskId)) {
      setSelectedIds(selectedIds.filter(id => id !== taskId));
    } else {
      setSelectedIds([...selectedIds, taskId]);
    }
  };

  const bulkMarkResolved = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`Marquer ${selectedIds.length} signalement(s) comme traité(s) ?`)) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/signalements/bulk-resolve`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ task_ids: selectedIds })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.resolved_count} signalement(s) traité(s)`);
        setSelectedIds([]);
        fetchSignalements();
      }
    } catch (error) {
      console.error('Error bulk resolving:', error);
      toast.error('Erreur lors du traitement groupé');
    } finally {
      setActionLoading(false);
    }
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    if (!window.confirm(`⚠️ Supprimer définitivement ${selectedIds.length} signalement(s) ? Cette action est irréversible.`)) return;
    
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/signalements/bulk-delete`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ task_ids: selectedIds })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.deleted_count} signalement(s) supprimé(s)`);
        setSelectedIds([]);
        fetchSignalements();
      }
    } catch (error) {
      console.error('Error bulk deleting:', error);
      toast.error('Erreur lors de la suppression groupée');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredSignalements = signalements.filter(s => {
    const matchesSearch = 
      s.property_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.housekeeper_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.problem_text?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = 
      filterStatus === 'all' ||
      (filterStatus === 'pending' && !s.problem_resolved) ||
      (filterStatus === 'resolved' && s.problem_resolved);
    
    return matchesSearch && matchesFilter;
  });

  const pendingCount = signalements.filter(s => !s.problem_resolved).length;

  // Helper to get valid photo URL
  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    // If it's already a full URL, return as-is
    if (photo.startsWith('http://') || photo.startsWith('https://')) {
      return photo;
    }
    // Use the photo endpoint with query param
    const token = localStorage.getItem('token');
    return `${API_URL}/api/photo?path=${encodeURIComponent(photo)}&auth=${token}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#1F3A8A] text-white px-6 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="h-8 w-8" />
            <h1 className="text-2xl font-bold">Signalements</h1>
          </div>
          <p className="text-blue-200">
            {pendingCount > 0 
              ? `${pendingCount} signalement${pendingCount > 1 ? 's' : ''} en attente de traitement`
              : 'Tous les signalements ont été traités'
            }
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher par logement, FDM ou texte..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={filterStatus === 'all' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('all')}
                size="sm"
              >
                Tous ({signalements.length})
              </Button>
              <Button
                variant={filterStatus === 'pending' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('pending')}
                size="sm"
                className={filterStatus === 'pending' ? 'bg-amber-500 hover:bg-amber-600' : ''}
              >
                En attente ({pendingCount})
              </Button>
              <Button
                variant={filterStatus === 'resolved' ? 'default' : 'outline'}
                onClick={() => setFilterStatus('resolved')}
                size="sm"
                className={filterStatus === 'resolved' ? 'bg-emerald-500 hover:bg-emerald-600' : ''}
              >
                Traités ({signalements.length - pendingCount})
              </Button>
            </div>
          </div>
        </div>

        {/* Bulk Actions Bar */}
        {filteredSignalements.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleSelectAll}
                  className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-800"
                >
                  {selectedIds.length === filteredSignalements.length ? (
                    <CheckSquare className="h-5 w-5 text-[#003366]" />
                  ) : (
                    <Square className="h-5 w-5" />
                  )}
                  {selectedIds.length === filteredSignalements.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                </button>
                {selectedIds.length > 0 && (
                  <span className="text-sm text-slate-500">
                    {selectedIds.length} sélectionné{selectedIds.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
              
              {selectedIds.length > 0 && (
                <div className="flex gap-2">
                  <Button
                    onClick={bulkMarkResolved}
                    disabled={actionLoading}
                    size="sm"
                    className="bg-emerald-500 hover:bg-emerald-600"
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <CheckCircle className="h-4 w-4 mr-1" />
                    )}
                    Traiter ({selectedIds.length})
                  </Button>
                  <Button
                    onClick={bulkDelete}
                    disabled={actionLoading}
                    size="sm"
                    variant="destructive"
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-1" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-1" />
                    )}
                    Supprimer ({selectedIds.length})
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Signalements List */}
        {filteredSignalements.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <AlertTriangle className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-600 mb-2">Aucun signalement</h3>
            <p className="text-slate-400">
              {searchQuery || filterStatus !== 'all' 
                ? 'Aucun signalement ne correspond à vos critères'
                : 'Les signalements des FDM apparaîtront ici'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredSignalements.map((signalement) => (
              <div
                key={signalement.task_id}
                className={`bg-white rounded-xl shadow-sm border ${
                  signalement.problem_resolved 
                    ? 'border-emerald-200 bg-emerald-50/30' 
                    : 'border-amber-200 bg-amber-50/30'
                } overflow-hidden`}
              >
                <div className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <div className="pt-1">
                      <Checkbox
                        checked={selectedIds.includes(signalement.task_id)}
                        onCheckedChange={() => handleSelectOne(signalement.task_id)}
                      />
                    </div>
                    
                    <div className="flex-1">
                      {/* Status Badge */}
                      <div className="flex items-center gap-2 mb-3">
                        {signalement.problem_resolved ? (
                          <Badge className="bg-emerald-100 text-emerald-700 border-0">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Traité
                          </Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700 border-0">
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            En attente
                          </Badge>
                        )}
                        {signalement.problem_reported_at && (
                          <span className="text-xs text-slate-400">
                            Signalé le {format(parseISO(signalement.problem_reported_at), "d MMM yyyy 'à' HH:mm", { locale: fr })}
                          </span>
                        )}
                      </div>

                      {/* Property & Date */}
                      <div className="flex items-center gap-4 mb-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <Home className="h-4 w-4 text-[#003366]" />
                          <span className="font-semibold text-slate-800">{signalement.property_name || 'Logement inconnu'}</span>
                        </div>
                        {signalement.date && (
                          <div className="flex items-center gap-2 text-slate-500">
                            <Calendar className="h-4 w-4" />
                            <span className="text-sm">{format(parseISO(signalement.date), "d MMMM yyyy", { locale: fr })}</span>
                          </div>
                        )}
                      </div>

                      {/* FDM */}
                      <div className="flex items-center gap-2 mb-4">
                        <User className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-600">Signalé par {signalement.housekeeper_name || 'Inconnu'}</span>
                      </div>

                      {/* Problem Text */}
                      {signalement.problem_text && (
                        <div className="bg-white rounded-lg p-4 border border-slate-200 mb-4">
                          <div className="flex items-start gap-2">
                            <MessageSquare className="h-4 w-4 text-slate-400 mt-1 flex-shrink-0" />
                            <p className="text-slate-700">{signalement.problem_text}</p>
                          </div>
                        </div>
                      )}

                      {/* Photos */}
                      {signalement.problem_photos && signalement.problem_photos.length > 0 && (
                        <div className="mt-4">
                          <p className="text-sm font-medium text-slate-600 mb-2 flex items-center gap-2">
                            <Image className="h-4 w-4" />
                            {signalement.problem_photos.length} photo{signalement.problem_photos.length > 1 ? 's' : ''} jointe{signalement.problem_photos.length > 1 ? 's' : ''}
                          </p>
                          <div className="flex gap-2 flex-wrap">
                            {signalement.problem_photos.map((photo, idx) => {
                              const photoUrl = getPhotoUrl(photo);
                              const photoKey = `${signalement.id}-${typeof photo === 'string' ? photo : (photo?.id || photo?.path || idx)}`;
                              if (!photoUrl) {
                                return (
                                  <div
                                    key={photoKey}
                                    className="w-20 h-20 bg-slate-100 rounded-lg flex items-center justify-center border border-slate-200"
                                  >
                                    <Image className="h-6 w-6 text-slate-300" />
                                  </div>
                                );
                              }
                              return (
                                <img
                                  key={photoKey}
                                  src={photoUrl}
                                  alt={`Photo ${idx + 1}`}
                                  className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border border-slate-200"
                                  onClick={() => {
                                    setSelectedPhoto(photoUrl);
                                    setShowPhotoDialog(true);
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = '';
                                    e.target.className = 'hidden';
                                  }}
                                />
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/admin/tasks/${signalement.task_id}`)}
                        className="whitespace-nowrap"
                      >
                        Voir la tâche
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                      {!signalement.problem_resolved && (
                        <Button
                          size="sm"
                          onClick={() => markAsResolved(signalement.task_id)}
                          className="bg-emerald-500 hover:bg-emerald-600 whitespace-nowrap"
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Traiter
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          if (window.confirm('Supprimer ce signalement ?')) {
                            deleteSignalement(signalement.task_id);
                          }
                        }}
                        className="text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Supprimer
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Photo Dialog */}
      <Dialog open={showPhotoDialog} onOpenChange={setShowPhotoDialog}>
        <DialogContent className="sm:max-w-3xl p-0 overflow-hidden">
          <div className="relative">
            <button
              onClick={() => setShowPhotoDialog(false)}
              className="absolute top-2 right-2 bg-black/50 text-white rounded-full p-1 hover:bg-black/70 z-10"
            >
              <X className="h-5 w-5" />
            </button>
            {selectedPhoto && (
              <img
                src={selectedPhoto}
                alt="Photo agrandie"
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Signalements;
